import { createContext, useContext, useState, useEffect, useRef } from "react";
import { getApi, putApi } from "../pages/AppApis";

const ProfileContext = createContext();

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error("useProfile must be used within a ProfileProvider");
    }
    return context;
};

export const ProfileProvider = ({ children }) => {
    const [profileState, setProfileState] = useState({
        profileData: null,
        loading: false,
        error: null,
    });
    const hasInitialized = useRef(false);

    const setProfileData = (payload) => {
        setProfileState((prev) => ({
            ...prev,
            profileData: payload,
            loading: false,
            error: null,
        }));
    };

    const updateProfileSuccess = (payload) => {
        setProfileState((prev) => ({
            ...prev,
            profileData: payload,
            loading: false,
            error: null,
        }));
    };

    const updateProfileFailure = (payload) => {
        setProfileState((prev) => ({
            ...prev,
            loading: false,
            error: payload,
        }));
    };

    const setLoading = (loading) => {
        setProfileState((prev) => ({
            ...prev,
            loading,
        }));
    };

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const profileUrl = `/user/profile`;
            const response = await getApi(profileUrl);

            // Handle different response structures
            let data = response.response;

            // Debug log to track response structure
            if (process.env.NODE_ENV === "development") {
                console.log("Profile API Response Structure:", {
                    hasConfig: !!(data && data.config),
                    hasHeaders: !!(data && data.headers),
                    hasData: !!(data && data.data),
                    dataType: typeof data,
                    keys: data ? Object.keys(data) : [],
                });
            }

            // If data has axios response structure, extract the actual data
            if (data && typeof data === "object" && data.data) {
                data = data.data;
            }
            // If data has config, headers, etc (full axios response), extract the data
            else if (data && typeof data === "object" && data.config && data.headers) {
                data = data.data;
            }

            // Only set data if we have valid data
            if (data && typeof data === "object" && !data.error) {
                setProfileData(data);
            } else {
                console.error("Invalid profile data received:", data);
                setProfileState((prev) => ({
                    ...prev,
                    loading: false,
                    error: "Invalid profile data received",
                }));
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            setProfileState((prev) => ({
                ...prev,
                loading: false,
                error: error.message,
            }));
        }
    };

    const updateProfile = async (formData) => {
        setLoading(true);
        const updateUrl = `/user/update`;

        try {
            const response = await putApi(updateUrl, formData, true);
            if (response.response) {
                let data = response.response;

                // Handle different response structures
                if (data && typeof data === "object" && data.data) {
                    data = data.data;
                }
                // If data has config, headers, etc (full axios response), extract the data
                else if (data && typeof data === "object" && data.config && data.headers) {
                    data = data.data;
                }

                // Fetch fresh profile data after update
                await fetchProfile();
                return data;
            } else {
                const error = await response.json();
                throw new Error(error?.error || "Failed to update profile");
            }
        } catch (error) {
            updateProfileFailure(error.message);
            throw error;
        }
    };

    // Auto-fetch profile on mount if user is authenticated
    useEffect(() => {
        const autoFetchProfile = async () => {
            const token = localStorage.getItem("authUser");
            if (token && !hasInitialized.current) {
                hasInitialized.current = true;
                console.log("Auto-fetching profile data...");
                await fetchProfile();
            }
        };

        autoFetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const value = {
        ...profileState,
        setProfileData,
        updateProfileSuccess,
        updateProfileFailure,
        setLoading,
        fetchProfile,
        updateProfile,
    };

    return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
